/* import __COLOCATED_TEMPLATE__ from './test-settings-modal.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import type { PlaygroundSettings } from 'embercom/lib/fin-playground/settings';
import type UserSummary from 'embercom/objects/inbox/user-summary';
import { tracked } from '@glimmer/tracking';
import type InboxSearchSuggestionsService from 'embercom/services/inbox-search-suggestions-service';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
// @ts-ignore
import PredicateGroup from 'predicates/models/predicate-group';
// @ts-ignore
import { copy } from 'ember-copy';
import { localCopy } from 'tracked-toolbox';
interface TestSettingsModalArgs {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  playgroundSettings: PlaygroundSettings;
  withBrandDropdown?: boolean;
  withUserDropdown?: boolean;
  storedUser: UserSummary | null;
}

export default class TestSettingsModal extends Component<TestSettingsModalArgs> {
  @localCopy('args.storedUser') declare user: UserSummary | null;
  @service declare inboxSearchSuggestionsService: InboxSearchSuggestionsService;
  @service declare store: Store;
  @tracked ruleset: $TSFixMe;
  @tracked inSearchMode = false;
  @service declare appService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;

  constructor(owner: any, args: TestSettingsModalArgs) {
    super(owner, args);

    this.ruleset = this.setUpRuleset();
  }

  get userTargetingEnabled() {
    return this.args.withUserDropdown;
  }

  get brandIsDisabled() {
    return this.userTargetingEnabled && !this.user;
  }

  @action
  save() {
    this.args.onSave();
    this.onClose();

    this.intercomEventService.trackAnalyticsEvent({
      action: 'saved',
      object: 'playground_test_settings',
      place: 'playground_test_settings_modal',
    });
  }

  @action
  toggleSearchMode() {
    this.inSearchMode = !this.inSearchMode;

    if (this.inSearchMode) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'opened',
        object: 'impersonated_user_search',
        place: 'playground_test_settings_modal',
      });
    }
  }

  @action
  onCancel() {
    this.args.playgroundSettings.rollbackChanges();
    this.onClose();
  }

  @action
  onClose() {
    // reset the user to the stored user
    // yes, even on save, as the user might cancel the save in the next modal
    // if the save is completed, the storedUser will reflect the newest value
    this.user = this.args.storedUser;
    this.inSearchMode = false;
    this.args.onClose();
  }

  @action
  onSelectUser(user: UserSummary | null) {
    if (user) {
      this.args.playgroundSettings.userId = user.id;
      this.user = user;
    } else {
      this.args.playgroundSettings.userId = null;
      this.args.playgroundSettings.brandId = null;
      this.user = null;
    }

    this.intercomEventService.trackAnalyticsEvent({
      action: 'changed',
      object: 'user',
      place: 'playground_test_settings_modal',
    });
  }

  @action
  handleBrandSelection(brandId: string | null) {
    this.args.playgroundSettings.brandId = brandId;

    this.intercomEventService.trackAnalyticsEvent({
      action: 'changed',
      object: 'brand',
      place: 'playground_test_settings_modal',
    });
  }

  @action
  onUpdatePredicates(predicates: $TSFixMe) {
    this.ruleset.set('predicateGroup.predicates', copy(predicates));
    this.ruleset.fetchAudiencePreviewTask.perform();

    this.intercomEventService.trackAnalyticsEvent({
      action: 'changed',
      object: 'filters',
      place: 'playground_test_settings_modal',
    });
  }

  @action
  onUpdateRolePredicates(predicates: $TSFixMe) {
    this.ruleset.set('rolePredicateGroup.predicates', copy(predicates));
    this.ruleset.fetchAudiencePreviewTask.perform();

    this.intercomEventService.trackAnalyticsEvent({
      action: 'changed',
      object: 'role_filter',
      place: 'playground_test_settings_modal',
    });
  }

  setUpRuleset() {
    let newRuleset = this.store.createRecord('matching-system/ruleset');
    newRuleset.set('predicateGroup', this.store.createFragment('predicates/predicate-group'));
    newRuleset.set('clientPredicateGroup', this.store.createFragment('predicates/predicate-group'));
    // must properly define the default user roles to be all of them
    newRuleset.set(
      'rolePredicateGroup',
      this.store.createFragment('predicates/predicate-group', {
        predicates: PredicateGroup.convertRawPredicates(this.store, [
          {
            attribute: 'role',
            comparison: 'in',
            type: 'role',
            value: ['user_role', 'contact_role'],
          },
        ]),
      }),
    );
    newRuleset.set(
      'defaultPredicateGroup',
      this.store.createFragment('predicates/predicate-group'),
    );

    return newRuleset;
  }

  @action
  resetToDefaultSettings() {
    this.onSelectUser(null);
    this.handleBrandSelection(null);

    this.intercomEventService.trackAnalyticsEvent({
      action: 'reset',
      object: 'playground_test_settings',
      place: 'playground_test_settings_modal',
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Playground::Modals::TestSettingsModal': typeof TestSettingsModal;
  }
}
